import { ApiDataAccess } from '@softline/core';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SOFTLINE_API_MASTERDATA_ENTITY_QUERY } from '@softapps/core';
import { buildMasterdataQueryBody } from '../functions/masterdata-query.function';
import {
  MasterdataEntityDefinition,
  MasterdataQueryBody,
  MasterdataQueryOptions,
  MasterdataQueryResult,
} from '../types/masterdata-entity-definition';

/** Server-side search against a masterdata `/query` endpoint; one instance per
 *  {@link MasterdataEntityDefinition}. Plain class (no DI decorator) — construct
 *  directly or get one from `MasterdataService.repository()`. */
export class MasterdataEntityRepository<TDto> {
  constructor(
    private readonly dataAccess: ApiDataAccess,
    readonly definition: MasterdataEntityDefinition<TDto>
  ) {}

  /** Fuzzy search (Observable, cancelable) — prefer for live/typeahead search. */
  search$(
    searchTerm: string | null | undefined,
    options?: MasterdataQueryOptions
  ): Observable<TDto[]> {
    return this.searchWithCount$(searchTerm, options).pipe(map((r) => r.items));
  }

  /** Promise convenience over {@link search$} for one-shot lookups. */
  search(
    searchTerm: string | null | undefined,
    options?: MasterdataQueryOptions
  ): Promise<TDto[]> {
    return firstValueFrom(this.search$(searchTerm, options));
  }

  searchWithCount$(
    searchTerm: string | null | undefined,
    options?: MasterdataQueryOptions
  ): Observable<MasterdataQueryResult<TDto>> {
    return this.queryWithCount$(
      buildMasterdataQueryBody(this.definition, searchTerm, options)
    );
  }

  searchWithCount(
    searchTerm: string | null | undefined,
    options?: MasterdataQueryOptions
  ): Promise<MasterdataQueryResult<TDto>> {
    return firstValueFrom(this.searchWithCount$(searchTerm, options));
  }

  /** POSTs a prebuilt body and unwraps the entity-specific response array. */
  queryWithCount$(body: MasterdataQueryBody): Observable<MasterdataQueryResult<TDto>> {
    return this.dataAccess
      .get$<Record<string, unknown>>(
        {
          path: SOFTLINE_API_MASTERDATA_ENTITY_QUERY,
          pathParams: { entity: this.definition.endpoint ?? this.definition.name },
        },
        body
      )
      .pipe(
        map((result) => {
          const items = (result?.[this.definition.responseKey] as TDto[]) ?? [];
          const totalCount = result?.['totalCount'];
          return {
            items,
            totalCount: typeof totalCount === 'number' ? totalCount : items.length,
          };
        })
      );
  }
}
